import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Table, Input, Select, DatePicker, Button, Tag, Tooltip, message } from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  ClearOutlined,
  FileExcelOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  WalletOutlined,
  FieldTimeOutlined,
  CheckCircleOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { get_ListeClients } from "../../api/clients";
// ⚠️ Adapte le chemin / le nom à ta fonction qui renvoie la liste des échéances :
import { get_ListeEcheances } from "../../api/factures";

const { RangePicker } = DatePicker;

/* ───────────── Utilitaires ───────────── */
const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};
const fmtMoney = (v) => `${new Intl.NumberFormat("fr-FR").format(Math.round(v))} Ar`;
const fmtDate = (m) => m.format("DD/MM/YYYY");

/* limite_paiement peut être :
   - une date ("2026-11-12" ou "12/11/2026")  → c'est la date d'échéance
   - un nombre de jours ("30")                → échéance = date facture + 30 j
   Repli sur echeance_facture (jours) si absent. */
const resolveEcheance = (f, dateFacture, client) => {
  const lim = f.limite_paiement;
  if (lim !== undefined && lim !== null && String(lim).trim() !== "") {
    const s = String(lim).trim();
    if (/^\d+(\.\d+)?$/.test(s)) return dateFacture.clone().add(parseInt(s, 10), "days");
    const m = moment(s, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD", "DD/MM/YYYY"]);
    if (m.isValid()) return m.startOf("day");
  }
  const jours = f.echeance_facture != null && f.echeance_facture !== "" ? f.echeance_facture : client ? client.echeance_facture : 0;
  return dateFacture.clone().add(num(jours), "days");
};

const enrich = (f, client) => {
  const total = num(f.total_ttc);
  const acompte = num(f.total_acomptes) || num(f.acompte);
  const reste = f.reste_a_payer != null && f.reste_a_payer !== "" ? num(f.reste_a_payer) : total - acompte;
  const dateFacture = moment(f.date_facture).startOf("day");
  const echeance = resolveEcheance(f, dateFacture, client);
  const delai = echeance.diff(dateFacture, "days"); // nombre de jours accordés (limite_paiement − date facture)
  const jours = echeance.diff(moment().startOf("day"), "days"); // < 0 = en retard
  const client_nom = String(f.client_nom || (client && client.nom) || "—").trim();
  return { ...f, client_nom, key: f.id, total, acompte, reste, dateFacture, echeance, delai, jours };
};

const statutOf = (jours) => (jours < 0 ? "retard" : jours <= 7 ? "bientot" : "ok");
const STATUT_LABEL = { retard: "En retard", bientot: "Sous 7 jours", ok: "Dans les délais" };

const joursLabel = (j) => (j < 0 ? `${-j} j de retard` : j === 0 ? "Aujourd'hui" : `Dans ${j} j`);

/* ───────────── Export Excel ───────────── */
const exportExcel = (list) => {
  const th = (t) => `<th style="background:#3a5bc7;color:#fff;border:1px solid #2f4aa8">${t}</th>`;
  const bg = { retard: "#fde8e6", bientot: "#fff3dc", ok: "#e6f6ee" };
  const money = (v) => `<td align="right" style="mso-number-format:'#,##0'">${Math.round(v)}</td>`;
  const body = list
    .map((r) => {
      const s = statutOf(r.jours);
      return `<tr>
        <td>${r.client_nom}</td><td>${r.ref_facture}</td><td>${fmtDate(r.dateFacture)}</td><td>${fmtDate(r.echeance)}</td>
        <td align="center">${r.delai}</td>${money(r.total)}${money(r.acompte)}${money(r.reste)}
        <td style="background:${bg[s]}">${STATUT_LABEL[s]} (${joursLabel(r.jours)})</td></tr>`;
    })
    .join("");
  const total = (k) => Math.round(list.reduce((s, r) => s + r[k], 0));
  const html = `<html><head><meta charset="utf-8"></head><body>
    <table border="1" cellspacing="0" cellpadding="5" style="border-collapse:collapse;font-family:Arial;font-size:12px">
    <thead><tr>${[
      "Client",
      "ID de la facture",
      "Date de la facture",
      "Date d'échéance",
      "Délai (jours)",
      "Montant de la facture",
      "Acompte versé",
      "Reste à payer",
      "Statut",
    ]
      .map(th)
      .join("")}</tr></thead>
    <tbody>${body}
    <tr style="font-weight:bold;background:#eef1fd"><td colspan="5">Total (${list.length} facture${list.length > 1 ? "s" : ""})</td>
    <td align="right">${total("total")}</td><td align="right">${total("acompte")}</td><td align="right">${total("reste")}</td><td></td></tr>
    </tbody></table></body></html>`;
  const blob = new Blob(["\uFEFF" + html], { type: "application/vnd.ms-excel;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `echeances_clients_${moment().format("YYYYMMDD_HHmm")}.xls`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/* ───────────── Styles intégrés ───────────── */
const css = `
.ech-wrap{display:flex;flex-direction:column;gap:8px;height:100%;padding:8px 10px;background:#f1f3f8;font-size:12px;color:#1f2733;box-sizing:border-box;overflow:auto}

/* En-tête */
.ech-hero{display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:10px;padding:8px 14px;border-radius:12px;background:linear-gradient(105deg,#0b1426 0%,#1b2d5c 55%,#3a5bc7 100%);box-shadow:0 6px 18px rgba(27,45,92,.25);color:#fff}
.ech-hero-left{display:flex;align-items:center;gap:12px}
.ech-hero-icon{width:32px;height:32px;border-radius:9px;background:rgba(255,255,255,.14);display:flex;align-items:center;justify-content:center;font-size:18px}
.ech-title{margin:0;font-size:15px;font-weight:700;color:#fff;letter-spacing:-.01em}
.ech-sub{margin:0;color:rgba(255,255,255,.75);font-size:11.5px}
.ech-hero .ant-btn-primary{background:#5b7bf0;border-color:#5b7bf0;font-weight:600}
.ech-hero .ant-btn-primary:hover{background:#6f8cf5;border-color:#6f8cf5}

/* Bandeau d'alerte */
.ech-alert{display:flex;align-items:center;gap:10px;padding:9px 14px;border-radius:10px;background:#fdf8e3;border:1px solid #f1dd8f;color:#6b5311}
.ech-alert .anticon{font-size:18px;color:#e0a100}
.ech-alert b{color:#8a6500}
.ech-alert button{margin-left:auto;border:0;background:#e0a100;color:#fff;border-radius:6px;padding:3px 10px;font:inherit;font-weight:600;cursor:pointer}
.ech-alert button:focus-visible{outline:2px solid #6b5311;outline-offset:2px}

/* KPI dégradés */
.ech-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:8px}
.ech-kpi{position:relative;overflow:hidden;display:flex;align-items:center;gap:10px;border:0;border-radius:12px;padding:7px 12px;color:#fff;text-align:left;font:inherit;cursor:pointer;box-shadow:0 5px 14px rgba(0,0,0,.14);transition:transform .15s,box-shadow .15s}
.ech-kpi:hover{transform:translateY(-2px);box-shadow:0 8px 18px rgba(0,0,0,.2)}
.ech-kpi:focus-visible{outline:3px solid #1f2733;outline-offset:2px}
.ech-kpi::after{content:"";position:absolute;right:-30px;top:-40px;width:120px;height:120px;border-radius:50%;background:rgba(255,255,255,.12)}
.ech-kpi.is-active{box-shadow:0 0 0 3px #fff,0 0 0 5px #1f2733}
.ech-kpi-ico{flex:none;width:30px;height:30px;border-radius:9px;background:rgba(255,255,255,.2);display:flex;align-items:center;justify-content:center;font-size:15px}
.ech-kpi-label{font-size:11.5px;opacity:.9}
.ech-kpi-value{font-size:16px;font-weight:700;font-variant-numeric:tabular-nums;line-height:1.2}
.ech-kpi-meta{font-size:11px;opacity:.85}
.ech-kpi.total{background:linear-gradient(135deg,#6577ee,#3f4fd6)}
.ech-kpi.retard{background:linear-gradient(135deg,#d85a50,#a82f27)}
.ech-kpi.bientot{background:linear-gradient(135deg,#e6a63e,#d2692a)}
.ech-kpi.acomptes{background:linear-gradient(135deg,#63b2cd,#3f7f9b);cursor:default}
.ech-kpi.acomptes:hover{transform:none;box-shadow:0 5px 14px rgba(0,0,0,.14)}

/* Panneau filtres + table */
.ech-panel{flex:1;min-height:540px;background:#fff;border:1px solid #e1e5ee;border-radius:14px;padding:12px;display:flex;flex-direction:column;gap:10px;box-shadow:0 2px 8px rgba(31,39,51,.05)}
.ech-bar{display:flex;flex-wrap:wrap;gap:8px;align-items:center;justify-content:space-between}
.ech-filters{display:flex;flex-wrap:wrap;gap:8px;align-items:center}
.ech-excel.ant-btn{background:#1d8f5a;border-color:#1d8f5a;color:#fff;font-weight:600}
.ech-excel.ant-btn:hover:not([disabled]){background:#24a86a;border-color:#24a86a;color:#fff}
.ech-excel.ant-btn[disabled]{background:#f5f5f5;border-color:#d9d9d9;color:rgba(0,0,0,.25)}

.ech-wrap .ant-table{font-size:12px}
.ech-wrap .ant-table-thead>tr>th{padding:8px 10px;background:#eaeefc;color:#2a3a8c;font-weight:700;font-size:11.5px;border-bottom:2px solid #c9d3f7}
.ech-wrap .ant-table-tbody>tr>td{padding:6px 10px}
.ech-wrap .ant-table-summary td{padding:8px 10px;background:#eaeefc;color:#2a3a8c;font-weight:700}
.ech-row-retard>td{background:#fff3f2}
.ech-row-retard>td:first-child{box-shadow:inset 4px 0 0 #d1342f}
.ech-row-bientot>td{background:#fffaf0}
.ech-row-bientot>td:first-child{box-shadow:inset 4px 0 0 #e08a00}
.ech-row-ok>td:first-child{box-shadow:inset 4px 0 0 #2ea66a}
.ech-wrap .ant-table-tbody>tr:hover>td{background:#eef2ff !important}
.ech-num{font-variant-numeric:tabular-nums;white-space:nowrap}
.ech-client{font-weight:600;color:#26338f}
.ech-ref{color:#4a5565;font-variant-numeric:tabular-nums}
.ech-ech{display:flex;align-items:center;gap:6px;white-space:nowrap}
.ech-wrap .ant-tag{margin:0;font-size:10.5px;line-height:17px;padding:0 6px;border-radius:9px;font-weight:600}
.ech-acompte{color:#1d8f5a;font-weight:600}
.ech-reste{font-weight:700}
.ech-tablebox{flex:1;min-height:0;overflow:hidden}
.ech-actions{display:flex;gap:6px;align-items:center}
@media (max-width:1000px){.ech-kpis{grid-template-columns:repeat(2,1fr)}}
@media (max-width:600px){.ech-kpis{grid-template-columns:1fr}}
@media (prefers-reduced-motion:reduce){.ech-kpi{transition:none}.ech-kpi:hover{transform:none}}
`;

/* ───────────── Composant ───────────── */
const Echeances = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [search, setSearch] = useState("");
  const [client, setClient] = useState(undefined);
  const [statut, setStatut] = useState("tous");
  const [range, setRange] = useState(null);
  const [focus, setFocus] = useState(false); // mode tableau : masque en-tête et cartes
  const boxRef = useRef(null);
  const [boxH, setBoxH] = useState(480);
  useEffect(() => {
    if (!boxRef.current || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([e]) => setBoxH(e.contentRect.height));
    ro.observe(boxRef.current);
    return () => ro.disconnect();
  }, []);
  // la table occupe tout l'espace restant, avec un minimum de 10 lignes (~33 px chacune)
  const tableY = Math.max(boxH - 125, 330);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, resClients] = await Promise.all([
        get_ListeEcheances(),
        get_ListeClients().catch(() => null), // la liste des clients alimente seulement le filtre
      ]);
      const listeClients = resClients && resClients.status === "success" ? resClients.data || [] : [];
      setClients(listeClients);
      const byId = {};
      listeClients.forEach((c) => {
        byId[String(c.id)] = c;
      });
      if (res && res.status === "success") {
        // Seules les factures non soldées sont des échéances à suivre
        setRows((res.data || []).map((f) => enrich(f, byId[String(f.client_id)])).filter((r) => r.reste > 0));
        setUpdatedAt(moment());
      } else {
        message.error((res && res.description) || "Impossible de charger les échéances");
      }
    } catch (e) {
      message.error("Erreur lors du chargement des échéances");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const clientOptions = useMemo(() => {
    const src = clients.length
      ? clients.map((c) => ({ id: String(c.id), nom: String(c.nom || "").trim() }))
      : [...new Map(rows.map((r) => [String(r.client_id), { id: String(r.client_id), nom: r.client_nom }])).values()];
    const count = {};
    src.forEach((c) => {
      count[c.nom.toLowerCase()] = (count[c.nom.toLowerCase()] || 0) + 1;
    });
    return src
      .filter((c) => c.nom)
      .map((c) => ({ value: c.id, label: count[c.nom.toLowerCase()] > 1 ? `${c.nom} (#${c.id})` : c.nom }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [clients, rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => {
        if (q && !`${r.client_nom} ${r.ref_facture}`.toLowerCase().includes(q)) return false;
        if (client && String(r.client_id) !== client) return false;
        if (statut !== "tous" && statutOf(r.jours) !== statut) return false;
        if (range && range[0] && range[1]) {
          if (r.echeance.isBefore(range[0], "day") || r.echeance.isAfter(range[1], "day")) return false;
        }
        return true;
      })
      .sort((a, b) => a.echeance.valueOf() - b.echeance.valueOf());
  }, [rows, search, client, statut, range]);

  const sum = (list, k) => list.reduce((s, r) => s + r[k], 0);
  const retards = useMemo(() => rows.filter((r) => statutOf(r.jours) === "retard"), [rows]);
  const bientot = useMemo(() => rows.filter((r) => statutOf(r.jours) === "bientot"), [rows]);
  const plural = (n, w) => `${n} ${w}${n > 1 ? "s" : ""}`;

  const hasFilters = search || client || statut !== "tous" || range;
  const reset = () => {
    setSearch("");
    setClient(undefined);
    setStatut("tous");
    setRange(null);
  };
  const toggleStatut = (s) => setStatut((cur) => (cur === s ? "tous" : s));

  const onExport = () => {
    exportExcel(filtered);
    message.success("Export Excel généré");
  };

  /* ───── Colonnes ───── */
  const columns = [
    {
      title: "Client",
      dataIndex: "client_nom",
      key: "client_nom",
      width: 150,
      sorter: (a, b) => a.client_nom.localeCompare(b.client_nom),
      render: (v) => <span className="ech-client">{v}</span>,
    },
    {
      title: "ID de la facture",
      dataIndex: "ref_facture",
      key: "ref_facture",
      width: 165,
      render: (v) => <span className="ech-ref">{v}</span>,
    },
    {
      title: "Date de la facture",
      dataIndex: "dateFacture",
      key: "dateFacture",
      width: 110,
      sorter: (a, b) => a.dateFacture.valueOf() - b.dateFacture.valueOf(),
      render: (m) => fmtDate(m),
    },
    {
      title: "Délai accordé",
      dataIndex: "delai",
      key: "delai",
      width: 90,
      align: "center",
      sorter: (a, b) => a.delai - b.delai,
      render: (v) => (
        <Tooltip title="Limite de paiement − date de la facture">
          <Tag color="geekblue">{v} jours</Tag>
        </Tooltip>
      ),
    },
    {
      title: "Date d'échéance convenue",
      dataIndex: "echeance",
      key: "echeance",
      width: 200,
      defaultSortOrder: "ascend",
      sorter: (a, b) => a.echeance.valueOf() - b.echeance.valueOf(),
      render: (m, r) => {
        const s = statutOf(r.jours);
        return (
          <span className="ech-ech">
            <b>{fmtDate(m)}</b>
            <Tag color={s === "retard" ? "red" : s === "bientot" ? "orange" : "green"}>{joursLabel(r.jours)}</Tag>
          </span>
        );
      },
    },
    {
      title: "Montant de la facture",
      dataIndex: "total",
      key: "total",
      align: "right",
      width: 125,
      sorter: (a, b) => a.total - b.total,
      render: (v) => <span className="ech-num">{fmtMoney(v)}</span>,
    },
    {
      title: "Acompte versé",
      dataIndex: "acompte",
      key: "acompte",
      align: "right",
      width: 115,
      sorter: (a, b) => a.acompte - b.acompte,
      render: (v) => <span className="ech-num ech-acompte">{v > 0 ? fmtMoney(v) : "—"}</span>,
    },
    {
      title: "Reste à payer",
      dataIndex: "reste",
      key: "reste",
      align: "right",
      width: 125,
      sorter: (a, b) => a.reste - b.reste,
      render: (v, r) => {
        const s = statutOf(r.jours);
        return (
          <span className="ech-num ech-reste" style={{ color: s === "retard" ? "#b42a26" : s === "bientot" ? "#b36b00" : "#26338f" }}>
            {fmtMoney(v)}
          </span>
        );
      },
    },
  ];

  return (
    <div className="ech-wrap">
      <style>{css}</style>

      {!focus && (
        <>
      <div className="ech-hero">
        <div className="ech-hero-left">
          <div className="ech-hero-icon">
            <CalendarOutlined />
          </div>
          <div>
            <h2 className="ech-title">Échéances clients</h2>
            <p className="ech-sub">
              {plural(rows.length, "facture")} impayée{rows.length > 1 ? "s" : ""}
              {updatedAt ? ` · mis à jour à ${updatedAt.format("HH:mm")}` : ""}
            </p>
          </div>
        </div>
        <Button type="primary" icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Rafraîchir
        </Button>
      </div>

      <div className="ech-kpis">
        <button type="button" className={`ech-kpi total ${statut === "tous" ? "is-active" : ""}`} onClick={() => setStatut("tous")}>
          <span className="ech-kpi-ico"><WalletOutlined /></span>
          <span>
            <div className="ech-kpi-label">Total à encaisser</div>
            <div className="ech-kpi-value">{fmtMoney(sum(rows, "reste"))}</div>
            <div className="ech-kpi-meta">{plural(rows.length, "facture")}</div>
          </span>
        </button>
        <button type="button" className={`ech-kpi retard ${statut === "retard" ? "is-active" : ""}`} onClick={() => toggleStatut("retard")}>
          <span className="ech-kpi-ico"><ExclamationCircleOutlined /></span>
          <span>
            <div className="ech-kpi-label">En retard</div>
            <div className="ech-kpi-value">{fmtMoney(sum(retards, "reste"))}</div>
            <div className="ech-kpi-meta">{plural(retards.length, "facture")}</div>
          </span>
        </button>
        <button type="button" className={`ech-kpi bientot ${statut === "bientot" ? "is-active" : ""}`} onClick={() => toggleStatut("bientot")}>
          <span className="ech-kpi-ico"><FieldTimeOutlined /></span>
          <span>
            <div className="ech-kpi-label">À échéance sous 7 jours</div>
            <div className="ech-kpi-value">{fmtMoney(sum(bientot, "reste"))}</div>
            <div className="ech-kpi-meta">{plural(bientot.length, "facture")}</div>
          </span>
        </button>
        <div className="ech-kpi acomptes">
          <span className="ech-kpi-ico"><CheckCircleOutlined /></span>
          <span>
            <div className="ech-kpi-label">Acomptes déjà versés</div>
            <div className="ech-kpi-value">{fmtMoney(sum(rows, "acompte"))}</div>
            <div className="ech-kpi-meta">sur {fmtMoney(sum(rows, "total"))} facturés</div>
          </span>
        </div>
      </div>

        </>
      )}

      <div className="ech-panel">
        <div className="ech-bar">
          <div className="ech-filters">
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Client ou ID de facture"
              style={{ width: 210 }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select
              allowClear
              showSearch
              placeholder="Tous les clients"
              style={{ width: 190 }}
              options={clientOptions}
              value={client}
              onChange={setClient}
              optionFilterProp="label"
            />
            <Select
              style={{ width: 170 }}
              value={statut}
              onChange={setStatut}
              options={[
                { value: "tous", label: "Toutes les échéances" },
                { value: "retard", label: "En retard" },
                { value: "bientot", label: "Sous 7 jours" },
                { value: "ok", label: "Dans les délais" },
              ]}
            />
            <RangePicker
              format="DD/MM/YYYY"
              placeholder={["Échéance du", "au"]}
              value={range}
              onChange={setRange}
              style={{ width: 240 }}
            />
            {hasFilters && (
              <Button type="link" icon={<ClearOutlined />} onClick={reset}>
                Réinitialiser
              </Button>
            )}
          </div>
          <div className="ech-actions">
            <Tooltip title="Actualiser">
              <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
            </Tooltip>
            <Tooltip title={focus ? "Afficher les indicateurs" : "Agrandir le tableau"}>
              <Button
                icon={focus ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={() => setFocus((v) => !v)}
              />
            </Tooltip>
            <Button className="ech-excel" icon={<FileExcelOutlined />} onClick={onExport} disabled={!filtered.length}>
              Exporter en Excel
            </Button>
          </div>
        </div>

        <div className="ech-tablebox" ref={boxRef}>
        <Table
          size="small"
          loading={loading}
          columns={columns}
          dataSource={filtered}
          rowClassName={(r) => `ech-row-${statutOf(r.jours)}`}
          scroll={{ x: 1080, y: tableY }}
          pagination={{
            size: "small",
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (t) => plural(t, "facture"),
          }}
          locale={{ emptyText: hasFilters ? "Aucune échéance ne correspond à ces filtres" : "Aucune facture impayée" }}
          summary={() =>
            filtered.length ? (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={5}>
                    Total filtré
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <span className="ech-num">{fmtMoney(sum(filtered, "total"))}</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <span className="ech-num">{fmtMoney(sum(filtered, "acompte"))}</span>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <span className="ech-num">{fmtMoney(sum(filtered, "reste"))}</span>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            ) : null
          }
        />
        </div>
      </div>
    </div>
  );
};

export default Echeances;